import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  XMarkIcon,
  ClockIcon,
  UserIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  CogIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import { adminApiService } from '../../../../shared/services/adminApi'
import toast from 'react-hot-toast'

interface PlanHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  userId: string
  userEmail: string
}

interface PlanHistoryItem {
  id: string
  oldPlan: string
  newPlan: string
  reason: string
  method: string
  changedAt: string
  changedBy: {
    email: string
    name?: string
  } | null
  metadata: any
}

interface PaginationInfo {
  currentPage: number
  totalPages: number
  totalCount: number
  hasNext: boolean
  hasPrev: boolean
}

export default function PlanHistoryModal({ isOpen, onClose, userId, userEmail }: PlanHistoryModalProps) {
  const [planHistory, setPlanHistory] = useState<PlanHistoryItem[]>([])
  const [pagination, setPagination] = useState<PaginationInfo | null>(null)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    if (isOpen && userId) {
      loadPlanHistory()
    }
  }, [isOpen, userId, currentPage])

  const loadPlanHistory = async () => {
    try {
      setLoading(true)
      const data = await adminApiService.getUserPlanHistory(userId, {
        page: currentPage,
        limit: 20
      })
      
      setPlanHistory(data.planHistory || [])
      setPagination(data.pagination)
      setCurrentUser(data.user)
    } catch (error) {
      console.error('Failed to load plan history:', error)
      toast.error('Failed to load plan history')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getPlanColor = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'free':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      case 'growth':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      case 'scale':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
      case 'enterprise':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'admin_manual':
        return <CogIcon className="h-4 w-4" />
      case 'subscription':
        return <UserIcon className="h-4 w-4" />
      case 'upgrade':
        return <ArrowRightIcon className="h-4 w-4" />
      default:
        return <DocumentTextIcon className="h-4 w-4" />
    }
  }

  const getMethodDisplay = (method: string) => {
    switch (method) {
      case 'admin_manual':
        return 'Admin Change'
      case 'subscription':
        return 'Subscription'
      case 'upgrade':
        return 'User Upgrade'
      case 'downgrade':
        return 'User Downgrade'
      default:
        return method.charAt(0).toUpperCase() + method.slice(1)
    }
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  if (!isOpen) {return null}

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Plan Change History
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {currentUser?.email || userEmail}
              {currentUser?.currentPlan && (
                <span className="ml-2">
                  Current Plan: 
                  <span className={`ml-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanColor(currentUser.currentPlan)}`}>
                    {currentUser.currentPlan}
                  </span>
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[70vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                Loading plan history...
              </span>
            </div>
          ) : planHistory.length === 0 ? (
            <div className="text-center py-12">
              <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Plan Changes
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                This user has not made any plan changes yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {planHistory.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {/* Plan Change Visualization */}
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanColor(item.oldPlan)}`}>
                            {item.oldPlan}
                          </span>
                          <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanColor(item.newPlan)}`}>
                            {item.newPlan}
                          </span>
                        </div>

                        {/* Method Badge */}
                        <div className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400">
                          {getMethodIcon(item.method)}
                          <span>{getMethodDisplay(item.method)}</span>
                        </div>
                      </div>

                      {/* Reason */}
                      {item.reason && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                          <strong>Reason:</strong> {item.reason}
                        </p>
                      )}

                      {/* Changed By */}
                      {item.changedBy && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                          <strong>Changed by:</strong> {item.changedBy.name || item.changedBy.email}
                        </p>
                      )}

                      {/* Metadata */}
                      {item.metadata && Object.keys(item.metadata).length > 0 && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {item.metadata.oldLimits && item.metadata.newLimits && (
                            <div className="grid grid-cols-2 gap-4 mt-2">
                              <div>
                                <span className="font-medium">Previous Limits:</span>
                                <div className="text-xs mt-1">
                                  Links: {item.metadata.oldLimits.links === 999999 ? 'Unlimited' : item.metadata.oldLimits.links?.toLocaleString()}<br />
                                  Clicks: {item.metadata.oldLimits.clicks === 999999 ? 'Unlimited' : item.metadata.oldLimits.clicks?.toLocaleString()}
                                </div>
                              </div>
                              <div>
                                <span className="font-medium">New Limits:</span>
                                <div className="text-xs mt-1">
                                  Links: {item.metadata.newLimits.links === 999999 ? 'Unlimited' : item.metadata.newLimits.links?.toLocaleString()}<br />
                                  Clicks: {item.metadata.newLimits.clicks === 999999 ? 'Unlimited' : item.metadata.newLimits.clicks?.toLocaleString()}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Timestamp */}
                    <div className="text-sm text-gray-500 dark:text-gray-400 text-right">
                      <div className="flex items-center space-x-1">
                        <ClockIcon className="h-4 w-4" />
                        <span>{formatDate(item.changedAt)}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing page {pagination.currentPage} of {pagination.totalPages} 
                <span className="ml-2">({pagination.totalCount} total changes)</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrev}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.currentPage}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}